import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from './NotificationContext';
import { useAuth } from './AuthContext';
import EventCardBack from './EventCardBack';
import { BACKEND_URL } from '../api/config';
import {
  getChannelDisplayName,
  getStreamTitle,
  formatEventDate,
  formatEventTimeRange,
} from '../utils/channelDisplay';
import { isOfficialChannelEvent } from '../utils/eventMatchKind';
import { goToSignup } from '../utils/guestSearch';
import { cssUrlValue, safeMediaUrl } from '../utils/mediaUrl';
import { eventUsesCoverFill } from '../utils/cardThumbArt';
import '../styles/flipCard.css';

const VerifiedBadge = () => (
  <span className="verified-badge" title="Official channel" aria-label="Official channel">
    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
      <path d="M12 1.6 14.6 4l3.5-.2.2 3.5L20.4 12l-2.1 4.7-.2 3.5-3.5-.2L12 22.4 9.4 20l-3.5.2-.2-3.5L3.6 12l2.1-4.7.2-3.5L9.4 4z" />
      <path d="m8.5 12 2.2 2.2 4.8-4.8" fill="none" stroke="#0d1424" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const HubBellIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const TicketIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 8h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4V8z" />
    <path d="M8 8v11" strokeDasharray="2 3" />
  </svg>
);

function resolveLogoSrc(logo) {
  if (!logo) return null;
  if (logo.startsWith('http://') || logo.startsWith('https://')) {
    return safeMediaUrl(logo);
  }
  if (logo.startsWith('/assets/')) return BACKEND_URL + logo;
  return safeMediaUrl(logo) || logo;
}

function isValidLogo(src) {
  return src
    && !src.includes('placehold.co/80x80?text=Logo')
    && !src.includes('placehold.co/80x80?text=No+Logo');
}

function PlatformCorner({ platform }) {
  if (platform === 'youtube') {
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="#FF0000" aria-hidden="true">
        <path d="M23.498 6.186a2.994 2.994 0 0 0-2.108-2.116C19.228 3.5 12 3.5 12 3.5s-7.228 0-9.39.57A2.994 2.994 0 0 0 .502 6.186C0 8.36 0 12 0 12s0 3.64.502 5.814a2.994 2.994 0 0 0 2.108 2.116C4.772 20.5 12 20.5 12 20.5s7.228 0 9.39-.57a2.994 2.994 0 0 0 2.108-2.116C24 15.64 24 12 24 12s0-3.64-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    );
  }
  if (platform === 'twitch') {
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="#9146FF" aria-hidden="true">
        <path d="M2.857 0L.286 4.286V21.43h5.143V24h3.429l3.429-2.571h4.286L23.714 16V0H2.857zm17.143 15.429l-2.571 2.571h-4.286l-3.429 2.571v-2.571H3.429V1.714h16.571v13.715z" />
      </svg>
    );
  }
  if (platform === 'kick') {
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="#53FC18" aria-hidden="true">
        <path d="M3 3h5.2v6.2L14.5 3H21l-7.1 7.4L21 21h-6.6l-6.2-7.8V21H3V3z" />
      </svg>
    );
  }
  if (platform === 'EPG') {
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#4ade80" strokeWidth="2" aria-hidden="true">
        <rect x="2" y="6" width="20" height="14" rx="2" />
        <path d="m8 2 4 4 4-4" />
      </svg>
    );
  }
  return null;
}

function CardBackdrop({ event }) {
  const src = resolveLogoSrc(event.logo || event.thumbnail);
  if (!isValidLogo(src)) return null;

  return (
    <div className="card-backdrop" aria-hidden="true">
      <div className="card-thumb-bg" style={{ backgroundImage: cssUrlValue(src) }} />
    </div>
  );
}

function CardThumb({ event, children, compact = false }) {
  const thumbSrc = resolveLogoSrc(event.thumbnail);
  const logoSrc = resolveLogoSrc(event.logo);
  const fillThumb = eventUsesCoverFill(event);
  const coverSrc = isValidLogo(thumbSrc) ? thumbSrc : logoSrc;
  const markSrc = fillThumb ? null : (isValidLogo(logoSrc) ? logoSrc : thumbSrc);
  const hasMark = isValidLogo(markSrc);
  const hasCover = fillThumb && isValidLogo(coverSrc);

  return (
    <div
      className={`card-thumb${compact ? ' card-thumb--compact' : ''}${fillThumb ? ' card-thumb--fill' : ''}`}
    >
      {(hasCover || hasMark) && (
        <div
          className="card-thumb-bg"
          style={{ backgroundImage: cssUrlValue(hasCover ? coverSrc : markSrc) }}
          aria-hidden="true"
        />
      )}
      {!fillThumb ? (
        <div className="card-logo">
          {hasMark ? (
            <img className="card-logo-img" src={markSrc} alt="" />
          ) : (
            <span className="card-logo-fallback" aria-hidden="true">
              {(event.title || '?').charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      ) : null}
      {children}
    </div>
  );
}

const FlipEventCard = ({
  event,
  onBackClick,
  isLive = false,
  compact = false,
  expanded = false,
  onExpand,
  onCollapse,
  staggerIndex = 0,
}) => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const {
    isEventSubscribed,
    getEventSubscription,
    subscribeToEvent,
    unsubscribe,
  } = useNotifications();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [showLoginHint, setShowLoginHint] = useState(false);
  const containerRef = useRef(null);

  const showCompact = compact && !expanded;
  const isEpg = event.platform === 'EPG';
  const isConcert = event.platform === 'concert';
  const isScheduleCard = isEpg || isConcert;
  const isPlatform = event.platform === 'youtube' || event.platform === 'twitch' || event.platform === 'kick';
  const eventId = event.dbId || (isEpg ? event.id : null);
  const isSubscribed = eventId ? isEventSubscribed(eventId) : false;
  const subscriptionData = eventId ? getEventSubscription(eventId) : null;
  const channelName = getChannelDisplayName(event);
  const showOfficialBadge = isOfficialChannelEvent(event) || Boolean(event.verified);
  const showTickets = isConcert && Boolean(event.url);
  const showWatch = !isConcert && ((isEpg && isLive) || (Boolean(onBackClick && event.url)));
  const showNotify = (isEpg && !isLive) || (isConcert && showCompact);
  const showConcertRemind = isConcert && !showCompact;

  useEffect(() => {
    if (!expanded || !containerRef.current) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    containerRef.current.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }, [expanded]);

  const handleOpenPanel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      setShowLoginHint(true);
      setTimeout(() => setShowLoginHint(false), 4000);
      return;
    }
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => setIsPanelOpen(false);

  const handleSubscribe = async (channels, isMainEvent) => {
    if (!eventId) throw new Error('Event ID is required');
    if (isSubscribed && subscriptionData) {
      await unsubscribe(subscriptionData.id);
    }
    // Deselecting the last channel means unsubscribe only: the backend
    // rejects empty channels arrays with 400 INVALID_INPUT.
    if (channels.length === 0) return;
    await subscribeToEvent(eventId, channels, isMainEvent);
  };

  const handleCardClick = (e) => {
    if (isPanelOpen) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (expanded && onCollapse) {
      onCollapse();
      return;
    }
    if (showCompact && onExpand) {
      onExpand();
    }
  };

  const notifyLabel = isSubscribed
    ? 'Enabled'
    : (isLive ? 'Notify' : 'Remind me');
  const eventDate = formatEventDate(event.start);
  const eventTimeRange = formatEventTimeRange(event.start, event.end);
  const streamTitle = getStreamTitle(event);
  const dateTimeLine = [eventDate, eventTimeRange].filter(Boolean).join(' · ');
  const metaLine = [dateTimeLine, channelName].filter(Boolean).join(' · ');

  const actions = (showWatch || showNotify || showTickets) ? (
    <div
      className={`card-actions${isPlatform || (showTickets && !showNotify) ? ' card-actions-single' : ''}${showCompact ? ' card-actions--compact' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {showTickets && (
        <a
          href={event.url}
          target="_blank"
          rel="noopener noreferrer"
          className="act act-watch"
        >
          <TicketIcon />
          Tickets
        </a>
      )}
      {showWatch && (
        event.url ? (
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="act act-watch"
            onClick={(e) => {
              if (isPanelOpen) e.preventDefault();
            }}
          >
            <PlayIcon />
            Watch
          </a>
        ) : (
          <button type="button" className="act act-watch" aria-label="Watch (coming soon)">
            <PlayIcon />
            Watch
          </button>
        )
      )}
      {showNotify && (
        <button
          type="button"
          className={`act act-notify${isSubscribed ? ' act-notify-active' : ''}`}
          onClick={handleOpenPanel}
          aria-label={`${isSubscribed ? 'Enabled' : (isLive ? 'Notify' : 'Remind me')} for this event`}
        >
          <BellIcon />
          {notifyLabel}
        </button>
      )}
    </div>
  ) : null;

  const containerClass = [
    'flip-card-container',
    showCompact ? 'flip-card-container--compact' : '',
    expanded ? 'flip-card-container--expand-in' : '',
    isPanelOpen ? 'is-panel-open' : '',
    showCompact && onExpand ? 'is-expandable' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      className={containerClass}
      style={{ '--stagger-i': staggerIndex }}
      onClick={handleCardClick}
    >
      <article className={`event-card-face${showCompact ? ' event-card-face--compact' : ''}`}>
        {isLive && !isPanelOpen && (
          <span className="live-dot" title="Live" aria-label="Live" />
        )}

        {showLoginHint && (
          <div className="card-login-hint" role="status">
            <span>Log in to get notified about this event</span>
            <button
              type="button"
              className="card-login-hint-cta"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToSignup(navigate);
              }}
            >
              Sign up
            </button>
          </div>
        )}

        <CardThumb event={event} compact={showCompact}>
          {showConcertRemind ? (
            <button
              type="button"
              className={`card-remind-corner${isSubscribed ? ' is-active' : ''}`}
              onClick={handleOpenPanel}
              aria-label={`${isSubscribed ? 'Enabled' : 'Remind me'} for this event`}
            >
              <HubBellIcon />
            </button>
          ) : null}

          {!showCompact && isLive && event.viewers && (
            <span className="badge-viewers">
              <EyeIcon />
              {event.viewers}
            </span>
          )}

          {!showCompact && event.platform && event.platform !== 'concert' && (
            <span className="platform-corner">
              <PlatformCorner platform={event.platform} />
            </span>
          )}
        </CardThumb>

        <div className={`card-body${showCompact ? ' card-body--compact' : ''}`}>
          {isScheduleCard && (
            <>
              <h3 className="card-title">{event.title}</h3>
              {showCompact ? (
                metaLine && <p className="card-datetime">{metaLine}</p>
              ) : (
                <>
                  {dateTimeLine && (
                    <p className="card-datetime">{dateTimeLine}</p>
                  )}
                  {channelName && (
                    <div className="card-channel">
                      {channelName}
                      {showOfficialBadge && <VerifiedBadge />}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {isPlatform && (
            <>
              {streamTitle && <h3 className="card-title">{streamTitle}</h3>}
              {channelName && (
                <div className="card-channel">
                  {channelName}
                  {showOfficialBadge && <VerifiedBadge />}
                </div>
              )}
            </>
          )}

          {!showCompact && actions}

          {expanded && onCollapse && (
            <button
              type="button"
              className="card-collapse-btn"
              onClick={(e) => {
                e.stopPropagation();
                onCollapse();
              }}
            >
              Back to list
            </button>
          )}
        </div>

        {showCompact && actions}

        {isPanelOpen && (
          <div
            className="card-notify-panel"
            role="dialog"
            aria-label="Notification preferences"
            onClick={(e) => e.stopPropagation()}
          >
            <CardBackdrop event={event} />
            <EventCardBack
              event={event}
              isSubscribed={isSubscribed}
              subscriptionData={subscriptionData}
              allowPush={Boolean(eventId)}
              onSubscribe={handleSubscribe}
              onCancel={handleClosePanel}
            />
          </div>
        )}
      </article>
    </div>
  );
};

export default FlipEventCard;
